import React from 'react'
import styled from '@emotion/styled'
import { isNil } from 'lodash-es'
import { Field } from '../inputs/Field'
import * as constants from 'view/styles/constants'
import { KeyCodes } from 'utils'
import { Locale, Translation } from 'locale'
import { Tooltip } from '../tooltip'
import { VariableSizeList, ListChildComponentProps } from 'react-window'

export type OptionKey = string | number | boolean | null | undefined

export interface ListboxOptions<T, K extends OptionKey> {
  title?: string | JSX.Element
  optionKeys: K[]
  excludeOptionKeys?: K[]
  noItemsText?: string
  nullItemText?: string

  getOption?: (optionKey: K) => T | null
  getOptionText?: (option: T) => string
  getOptionTooltip?: (option: T) => string
  getOptionDisabled?: (option: T) => boolean
  filterOption?: (optionKey: K, option: T, optionText: string) => boolean

  /* option handling */
  optionTemplate?: React.ComponentType<OptionTemplateProps<T, K>>
  onOptionClick?: (optionKey: K, option: T, evt: React.MouseEvent<HTMLElement>) => void
  onOptionDoubleClick?: (optionKey: K, option: T, evt: React.MouseEvent<HTMLElement>) => void
  optionColorPalette?: string[]
}

export interface ListboxProps<T, K extends OptionKey> extends ListboxOptions<T, K> {
  id?: string
  className?: string
  'aria-label'?: string
  readOnly?: boolean
  enableHighlight?: boolean
  value?: K | null | undefined
  onUpdate?: (optionKey: K, ...rest: any[]) => void
  isVirtualized?: boolean
}

interface ListboxState {
  highlightedOptionKey: OptionKey | null
  visibleOptionKeys: OptionKey[]
}

export interface OptionTemplateProps<T, K extends OptionKey> {
  option: T
  optionKey: K
  optionIndex: number
  optionText: string
  optionColor?: string
  selected: boolean
  highlighted: boolean
}

interface ListboxItemProps {
  optionKey: OptionKey
  optionIndex: number
  getIsSelected: (optionKey: OptionKey) => boolean
  getIsHighlighted: (optionKey: OptionKey) => boolean
  getOption: (optionKey: OptionKey) => any
  getOptionText: (optionKey: OptionKey) => any
  getOptionTooltip: (optionKey: OptionKey) => any
  getOptionDisabled: (optionKey: OptionKey) => any
  optionColorPalette: string[] | undefined
  onClick: (optionKey: OptionKey, option: any, evt: React.MouseEvent<HTMLElement>) => void
  onDoubleClick: (optionKey: OptionKey, option: any, evt: React.MouseEvent<HTMLElement>) => void
  OptionTemplate:
    | React.ComponentType<OptionTemplateProps<any, OptionKey>>
    | ((optionProps: OptionTemplateProps<any, OptionKey>) => React.JSX.Element)
}

export const ListboxTitle = styled.div`
  height: 26px;
`

export const ListboxItems = styled.ul`
  position: relative;
  display: block;
  height: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  outline: transparent;
`

export const ListboxItem = styled.li<{ optionDisabled: boolean }>`
  opacity: ${props => (props.optionDisabled ? '0.33' : '1')};
  pointer-events: ${props => (props.optionDisabled ? 'none' : 'initial')};
  height: 26px;
  font-weight: ${constants.FontWeight.SemiBold};
  font-size: 12px;
  display: flex;
  align-items: stretch;
  padding: 0;
  border-radius: 3px;
  border: none;
`

export const ListboxNoItems = styled(ListboxItem)`
  background-color: ${constants.CORE_PRIMARY};
  color: ${constants.CORE_PRIMARY_4};
  align-items: center;
  padding: 0 6px;
`

export class Listbox extends React.PureComponent<ListboxProps<any, OptionKey>, ListboxState> {
  constructor(props: ListboxProps<any, OptionKey>) {
    super(props)
    this.state = { highlightedOptionKey: null, visibleOptionKeys: this.getVisibleOptions(props) }
  }

  private listRef = React.createRef<VariableSizeList>()
  private rootRef = React.createRef<HTMLUListElement>()

  componentDidMount() {
    this.setHighlightedOption(this.props.value)
  }

  UNSAFE_componentWillReceiveProps(newProps: ListboxProps<any, OptionKey>) {
    if (newProps.value !== this.props.value || this.state.highlightedOptionKey === null) {
      this.setHighlightedOption(newProps.value)
    }

    this.setState({ visibleOptionKeys: this.getVisibleOptions(newProps) })
  }

  private getVisibleOptions(props: ListboxProps<any, OptionKey>): any[] {
    if (props.optionKeys) {
      return props.optionKeys
        .filter(optionKey =>
          props.filterOption ? props.filterOption(optionKey, this.getOption(optionKey), this.getOptionText(optionKey)) : true
        )
        .filter(optionKey => (props.excludeOptionKeys ? props.excludeOptionKeys.indexOf(optionKey) === -1 : true))
    } else {
      return []
    }
  }

  private getOption(optionKey: OptionKey): any {
    if (isNil(optionKey)) {
      return null
    } else if (this.props.getOption) {
      return this.props.getOption(optionKey)
    } else {
      return optionKey
    }
  }

  private getOptionText(optionKey: OptionKey): any {
    const { getOptionText, nullItemText } = this.props
    const option = this.getOption(optionKey)

    if (isNil(optionKey)) {
      return nullItemText || ''
    } else if (isNil(option)) {
      return `${optionKey}`
    } else if (getOptionText) {
      return getOptionText(option)
    } else {
      return option.toString()
    }
  }

  private getOptionTooltip(optionKey: OptionKey): any {
    const { getOptionTooltip } = this.props
    const option = this.getOption(optionKey)

    if (isNil(optionKey)) {
      return ''
    } else if (isNil(option)) {
      return ''
    } else if (getOptionTooltip) {
      return getOptionTooltip(option)
    } else {
      return ''
    }
  }

  private getOptionDisabled(optionKey: OptionKey): any {
    const option = this.getOption(optionKey)

    if (isNil(optionKey)) {
      return false
    } else if (isNil(option)) {
      return false
    } else if (this.props.getOptionDisabled) {
      return this.props.getOptionDisabled(option)
    } else {
      return false
    }
  }

  private getOptionIndex(optionKey: OptionKey | null | undefined) {
    return this.state.visibleOptionKeys.findIndex(visibleKey => visibleKey === optionKey)
  }

  private adjustScroll(highlightedIndex: number) {
    // For future reference, I tried using the scrollIntoView method, but it caused
    // the browser to scroll to the bottom of the page for an unknown reason.
    const li = this.rootRef.current && this.rootRef.current.getElementsByTagName('li')[highlightedIndex]
    const op = this.rootRef.current && this.rootRef.current.offsetParent

    if (li && op) {
      const liTop = li.offsetTop
      const opTop = op.scrollTop
      const liBottom = li.offsetTop + li.clientHeight
      const opBottom = op.scrollTop + op.clientHeight

      if (liTop < opTop || liBottom > opBottom) {
        op.scrollTop = liTop
      }
    }
  }

  private highlightNextOption() {
    let highlightedIndex = this.getOptionIndex(this.state.highlightedOptionKey) + 1
    if (highlightedIndex >= this.state.visibleOptionKeys.length) {
      highlightedIndex = 0
    }
    this.setHighlightedIndex(highlightedIndex)
  }

  private highlightPreviousOption() {
    let highlightedIndex = this.getOptionIndex(this.state.highlightedOptionKey) - 1
    if (highlightedIndex < 0) {
      highlightedIndex = this.state.visibleOptionKeys.length - 1
    }
    this.setHighlightedIndex(highlightedIndex)
  }

  private setHighlightedOption(optionKey: OptionKey | null | undefined) {
    if (this.props.enableHighlight) {
      if (isNil(optionKey)) {
        this.setHighlightedIndex(0)
      } else {
        const highlightedIndex = this.getOptionIndex(optionKey)

        if (highlightedIndex >= 0) {
          this.setHighlightedIndex(highlightedIndex)
        }
      }
    }
  }

  private setHighlightedIndex(highlightedIndex: number) {
    const highlightedOptionKey = this.state.visibleOptionKeys[highlightedIndex]
    this.setState({ highlightedOptionKey })
    if (highlightedIndex !== 0) {
      this.adjustScroll(highlightedIndex)
    }
  }

  private getIsHighlighted(optionKey: OptionKey) {
    return optionKey === this.state.highlightedOptionKey
  }

  private getIsSelected(optionKey: OptionKey) {
    const selectedOptionKey = this.props.value
    return !isNil(selectedOptionKey) && optionKey === selectedOptionKey
  }

  private handleOptionClick = (optionKey: OptionKey, option: any, evt: React.MouseEvent<HTMLElement>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate(optionKey)
    }
    if (this.props.onOptionClick) {
      this.props.onOptionClick(optionKey, option, evt)
    }
  }

  private handleOptionDoubleClick = (optionKey: OptionKey, option: any, evt: React.MouseEvent<HTMLElement>) => {
    const { onOptionDoubleClick } = this.props

    if (onOptionDoubleClick) {
      onOptionDoubleClick(optionKey, option, evt)
    }
  }

  public handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
    const keyCode = evt.keyCode
    const index = this.getOptionIndex(this.state.highlightedOptionKey)
    switch (keyCode) {
      case KeyCodes.ENTER:
        evt.preventDefault()
        evt.stopPropagation()
        if (this.props.onUpdate) {
          this.props.onUpdate(this.state.highlightedOptionKey)
        }
        break

      case KeyCodes.UP:
        evt.preventDefault()
        evt.stopPropagation()
        this.highlightPreviousOption()
        if (this.listRef.current && this.props.isVirtualized) {
          this.listRef.current.scrollToItem(index, 'center')
        }
        break

      case KeyCodes.DOWN:
        evt.preventDefault()
        evt.stopPropagation()
        this.highlightNextOption()
        if (this.listRef.current && this.props.isVirtualized) {
          this.listRef.current.scrollToItem(index, 'center')
        }
        break

      default:
        break
    }
  }

  private renderOption = (optionProps: OptionTemplateProps<any, OptionKey>) => {
    return <span>{optionProps.optionText}</span>
  }

  private makeRows = (): ListboxItemProps[] => {
    const { optionTemplate, optionColorPalette } = this.props

    return this.state.visibleOptionKeys.map((optionKey, optionIndex) => {
      const OptionTemplate = optionTemplate || this.renderOption

      return {
        optionKey,
        optionIndex,
        OptionTemplate,
        getOption: this.getOption.bind(this),
        getOptionText: this.getOptionText.bind(this),
        getOptionTooltip: this.getOptionTooltip.bind(this),
        getOptionDisabled: this.getOptionDisabled.bind(this),
        optionColorPalette,
        getIsSelected: this.getIsSelected.bind(this),
        getIsHighlighted: this.getIsHighlighted.bind(this),
        onClick: this.handleOptionClick.bind(this),
        onDoubleClick: this.handleOptionDoubleClick.bind(this)
      }
    })
  }

  render(): JSX.Element {
    const { className, title, 'aria-label': ariaLabel = 'Options List' } = this.props

    const rows = this.makeRows()

    if (!rows.length) {
      return (
        <ListboxItems aria-label="Options List" ref={this.rootRef} className={className} onKeyDown={this.handleKeyDown} role="listbox">
          <ListboxNoItems key="no-items" optionDisabled={false}>
            <span>{this.props.noItemsText || Locale.translate(Translation.SC_N_ITEMS, { count: 0 })}</span>
          </ListboxNoItems>
        </ListboxItems>
      )
    }

    if (this.props.isVirtualized) {
      return (
        <ListboxItems
          style={{ padding: 0 }}
          aria-label={ariaLabel}
          ref={this.rootRef}
          className={className}
          onKeyDown={this.handleKeyDown}
          role="listbox">
          {title && (typeof title === 'string' ? <ListboxTitle>{title}</ListboxTitle> : title)}

          <div style={{ minWidth: '200px' }}>
            <VariableSizeList
              height={this.state.visibleOptionKeys.length > 18 ? 400 : this.state.visibleOptionKeys.length * 23}
              itemCount={this.state.visibleOptionKeys.length}
              className={constants.VIRTUALIZATION}
              itemData={rows}
              itemSize={() => 23}
              width="100%"
              ref={this.listRef}>
              {VirtualizedRow}
            </VariableSizeList>
          </div>
        </ListboxItems>
      )
    } else {
      return (
        <ListboxItems aria-label={ariaLabel} ref={this.rootRef} className={className} onKeyDown={this.handleKeyDown} role="listbox">
          {title && (typeof title === 'string' ? <ListboxTitle>{title}</ListboxTitle> : title)}
          {rows.map(props => (
            <Row key={props.optionKey?.toString()} {...props} />
          ))}
        </ListboxItems>
      )
    }
  }
}

export const ListboxField = Field(Listbox)

const VirtualizedRow = ({ index, style, data }: ListChildComponentProps<ListboxItemProps[]>) => {
  const props = data?.[index]

  return (
    <div key={index} style={style}>
      <Row {...props} />
    </div>
  )
}

const Row = (props: ListboxItemProps) => {
  const {
    optionIndex,
    optionKey,
    OptionTemplate,
    getOption,
    getOptionText,
    getOptionTooltip,
    getOptionDisabled,
    optionColorPalette,
    getIsSelected,
    getIsHighlighted,
    onClick,
    onDoubleClick
  } = props

  const option = getOption(optionKey)
  const optionText = getOptionText(optionKey)
  const optionTooltip = getOptionTooltip(optionKey)
  const optionDisabled = getOptionDisabled(optionKey)
  const optionColor = optionColorPalette ? optionColorPalette[optionIndex % optionColorPalette.length] : undefined

  return (
    <Tooltip key={isNil(optionIndex) ? 'null' : optionIndex.toString()} tooltip={optionTooltip} placement="bottom-end">
      <div>
        <ListboxItem
          aria-disabled={optionDisabled}
          data-test-id={`ListboxItem-AddAssets-${optionText}`}
          onClick={evt => onClick(optionKey, option, evt)}
          onDoubleClick={evt => onDoubleClick(optionKey, option, evt)}
          optionDisabled={optionDisabled}>
          <OptionTemplate
            option={option}
            optionKey={optionKey}
            optionIndex={optionIndex}
            optionColor={optionColor}
            optionText={optionText}
            selected={getIsSelected(optionKey)}
            highlighted={getIsHighlighted(optionKey)}
          />
        </ListboxItem>
      </div>
    </Tooltip>
  )
}
